// ---------------------------------------------------------------------------
// DnD Email Editor V2 – HTML block code modal
//
// A focused code editor for html blocks: a Monaco editor on the left (with the
// same html language mode, tag completion, pretty-printer and find widget as the
// production HTML editor) and a live preview on the right. Opened on double-click
// of an html layer/element in place of the inline WYSIWYG. Edits are local to the
// modal and committed on Save (Cancel / Esc / backdrop discard them).
// ---------------------------------------------------------------------------

import {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { EditorProps, Monaco } from '@monaco-editor/react';
import { AlertTriangle, IndentIncrease, Search, Wand2, X } from 'lucide-react';
import { formatHtmlBlock } from './dnd-html-format';
import { findBlockIssues, normalizeHtmlBlock, type BlockIssue } from './dnd-html-normalize';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { injectWebFontLinks } from './dnd-webfonts';
import { substituteVariables } from '@/components/ftp-editor/ftp-editor-utils';

const MonacoEditor = lazyWithRetry(() => import('@monaco-editor/react'), 'monaco-editor');

type StandaloneEditor = Parameters<NonNullable<EditorProps['onMount']>>[0];

export interface DndHtmlCodeModalProps {
  isOpen: boolean;
  /** Current html content of the block being edited. */
  initialHtml: string;
  /** Merge-tag tokens substituted into the live preview. */
  variables?: Record<string, string>;
  /** Commit the edited html to the model and close. */
  onSave: (html: string) => void;
  /** Discard changes and close. */
  onClose: () => void;
}

const EDITOR_OPTIONS: NonNullable<EditorProps['options']> = {
  automaticLayout: true,
  minimap: { enabled: false },
  fontSize: 13,
  lineNumbersMinChars: 2,
  glyphMargin: false,
  lineDecorationsWidth: 4,
  wordWrap: 'on',
  tabSize: 2,
  folding: true,
  scrollBeyondLastLine: false,
  formatOnPaste: true,
  // Tag/attribute autocomplete: surface Monaco's html suggestions as you type
  // (e.g. typing "d" offers "div"), accept with Tab/Enter.
  quickSuggestions: { other: true, comments: true, strings: true },
  suggestOnTriggerCharacters: true,
  acceptSuggestionOnEnter: 'on',
  tabCompletion: 'on',
  wordBasedSuggestions: 'currentDocument',
};

export function DndHtmlCodeModal({
  isOpen,
  initialHtml,
  variables,
  onSave,
  onClose,
}: DndHtmlCodeModalProps) {
  const [doc, setDoc] = useState('');
  const [preview, setPreview] = useState('');
  // Why this block cannot be saved yet (full document / <style> / <link> …).
  // Recomputed on the same debounce as the preview: it parses the document, so
  // running it per keystroke would stutter on a large paste.
  const [issues, setIssues] = useState<BlockIssue[]>([]);
  // Outcome of the last clean-up, kept visible so nothing is dropped silently.
  const [cleanReport, setCleanReport] = useState<string | null>(null);
  const editorRef = useRef<StandaloneEditor | null>(null);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Latest save handler, reachable from the (stable) Monaco Ctrl+S command.
  const saveRef = useRef<() => void>(() => {});

  // The preview is an iframe, so it needs the web-font definitions itself — it
  // does not inherit the fonts the app loads (see injectWebFontLinks).
  const refreshPreview = useCallback(
    (html: string) => setPreview(injectWebFontLinks(substituteVariables(html, variables))),
    [variables],
  );

  const schedulePreview = useCallback(
    (html: string) => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
      previewTimer.current = setTimeout(() => {
        previewTimer.current = null;
        refreshPreview(html);
        setIssues(findBlockIssues(html));
      }, 250);
    },
    [refreshPreview],
  );

  // Seed the editor + preview from the block content each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    const formatted = formatHtmlBlock(initialHtml || '');
    setDoc(formatted);
    refreshPreview(formatted);
    setIssues(findBlockIssues(formatted));
    setCleanReport(null);
  }, [isOpen, initialHtml, refreshPreview]);

  useEffect(() => () => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
  }, []);

  const handleSave = useCallback(() => {
    const value = editorRef.current?.getModel()?.getValue() ?? doc;
    // Re-check against the live value rather than the debounced state, so a
    // fast Ctrl+S right after pasting still hits the gate.
    const found = findBlockIssues(value);
    if (found.length > 0) {
      setIssues(found);
      return;
    }
    onSave(value);
  }, [doc, onSave]);
  saveRef.current = handleSave;

  // Strip the document wrapper and inline the stylesheet onto the elements.
  const handleClean = useCallback(() => {
    const ed = editorRef.current;
    const value = ed?.getModel()?.getValue() ?? doc;
    const { html, inlinedSelectors, droppedRules, removed, bakedMarkers } = normalizeHtmlBlock(value);
    const next = formatHtmlBlock(html);
    ed?.getModel()?.setValue(next);
    setDoc(next);
    refreshPreview(next);
    setIssues(findBlockIssues(next));

    const parts: string[] = [];
    if (removed.length) parts.push(`removed ${removed.join(', ')}`);
    if (inlinedSelectors) parts.push(`inlined ${inlinedSelectors} rule${inlinedSelectors === 1 ? '' : 's'}`);
    if (bakedMarkers) parts.push(`wrote ${bakedMarkers} generated marker${bakedMarkers === 1 ? '' : 's'} (list numbers/prefixes) into the text`);
    if (droppedRules.length) {
      const shown = droppedRules.slice(0, 4).join(', ');
      const more = droppedRules.length > 4 ? ` +${droppedRules.length - 4} more` : '';
      parts.push(`dropped ${droppedRules.length} rule${droppedRules.length === 1 ? '' : 's'} that cannot be inlined (${shown}${more})`);
    }
    setCleanReport(parts.length ? `Cleaned: ${parts.join(' · ')}.` : 'Nothing to clean.');
  }, [doc, refreshPreview]);

  const handleFormat = useCallback(() => {
    const ed = editorRef.current;
    const value = ed?.getModel()?.getValue() ?? doc;
    const next = formatHtmlBlock(value);
    ed?.getModel()?.setValue(next);
    setDoc(next);
    refreshPreview(next);
  }, [doc, refreshPreview]);

  const handleSearch = useCallback(() => {
    editorRef.current?.getAction('actions.find')?.run();
  }, []);

  const onMount = useCallback(
    (editor: StandaloneEditor, monaco: Monaco) => {
      editorRef.current = editor;
      editor.updateOptions(EDITOR_OPTIONS);
      // Ctrl/Cmd+S commits without leaving the keyboard.
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => saveRef.current());
    },
    [],
  );

  // Esc closes the modal (unless Monaco's find widget is handling it).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const el = document.activeElement as HTMLElement | null;
        if (el?.closest('.monaco-editor .find-widget')) return;
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="dnd-html-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="dnd-html-modal"
        role="dialog"
        aria-labelledby="dnd-html-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="dnd-html-modal-header">
          <h3 id="dnd-html-modal-title">Edit HTML</h3>
          <button type="button" className="dnd-html-modal-close" title="Close" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {issues.length > 0 && (
          <div className="dnd-html-modal-banner" role="alert">
            <AlertTriangle size={15} className="dnd-html-modal-banner-icon" />
            <div className="dnd-html-modal-banner-text">
              <strong>This block contains {issues.map((i) => i.label).join(', ')}.</strong>
              {' '}An email block holds body content only — a stylesheet here also restyles the
              editor page around it. Clean up HTML lifts out the body content and inlines the CSS
              onto the elements it styled. Rules that cannot be inlined (@media, :hover) are
              dropped and listed.
            </div>
            <button type="button" className="dnd-html-modal-banner-btn" onClick={handleClean}>
              <Wand2 size={13} /> Clean up HTML
            </button>
          </div>
        )}

        {issues.length === 0 && cleanReport && (
          <div className="dnd-html-modal-note">{cleanReport}</div>
        )}

        <div className="dnd-html-modal-body">
          <div className="dnd-html-modal-pane dnd-html-modal-code">
            <div className="dnd-html-modal-code-toolbar">
              <button type="button" className="dnd-html-modal-icon-btn" title="Format code" onClick={handleFormat}>
                <IndentIncrease size={13} />
              </button>
              <button type="button" className="dnd-html-modal-icon-btn" title="Search (Ctrl+F)" onClick={handleSearch}>
                <Search size={13} />
              </button>
            </div>
            <div className="dnd-html-modal-editor-wrap">
              <Suspense fallback={<div className="dnd-html-modal-loading">Loading editor…</div>}>
                <MonacoEditor
                  height="100%"
                  language="html"
                  theme="vs-dark"
                  value={doc}
                  onChange={(v) => {
                    const html = v ?? '';
                    setDoc(html);
                    schedulePreview(html);
                  }}
                  onMount={onMount}
                  options={EDITOR_OPTIONS}
                />
              </Suspense>
            </div>
          </div>
          <div className="dnd-html-modal-pane dnd-html-modal-preview">
            <div className="dnd-html-modal-pane-label">Preview</div>
            <iframe className="dnd-html-modal-frame" title="HTML preview" srcDoc={preview} />
          </div>
        </div>

        <div className="dnd-html-modal-footer">
          <button type="button" className="dnd-html-modal-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="dnd-html-modal-btn dnd-html-modal-btn-primary"
            onClick={handleSave}
            disabled={issues.length > 0}
            title={issues.length > 0 ? 'Clean up the HTML before saving' : undefined}
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
